import { NodeType, PageNode } from "../../types/PageNode";
import { OnClickAction } from "../../types/OnClickAction";
import AdultBadgeNodeFactory from "./AdultBadgeNodeFactory";
import {
  ADULT_BADGE_ATTRIBUTE,
  ADULT_BADGE_LAYOUT_ATTRIBUTE,
  ADULT_LINK_ATTRIBUTE,
  ADULT_URL_ATTRIBUTE,
  ADULT_VERIFIED_UI_CLASS,
} from "./constants";
import { getAdultVerificationDocuments } from "./dom";
import { isAdultOpenLink } from "./adultLinkService";

const VOID_ADULT_HOST_TAGS = new Set(["img", "input", "hr", "br"]);

export const isAdultBadgeNode = (node: PageNode): boolean =>
  node.attributes?.[ADULT_BADGE_ATTRIBUTE] === "true";

export const filterAdultBadgeNodes = (
  nodes: PageNode[] | undefined,
  hideBadges: boolean,
): PageNode[] => {
  if (!hideBadges || !nodes?.length) {
    return nodes ?? [];
  }

  return nodes.filter((node) => !isAdultBadgeNode(node));
};

const isVoidAdultHost = (node: PageNode): boolean =>
  node.type === NodeType.IconNode ||
  Boolean(node.tagName && VOID_ADULT_HOST_TAGS.has(node.tagName));

const attributesEqual = (
  left: PageNode["attributes"],
  right: PageNode["attributes"],
): boolean => {
  const leftKeys = Object.keys(left ?? {});
  const rightKeys = Object.keys(right ?? {});

  if (leftKeys.length !== rightKeys.length) {
    return false;
  }

  return leftKeys.every((key) => left?.[key] === right?.[key]);
};

const stripAdultAttributes = (
  attributes: PageNode["attributes"],
): PageNode["attributes"] => {
  if (!attributes) {
    return attributes;
  }

  const nextAttributes = { ...attributes };
  delete nextAttributes[ADULT_LINK_ATTRIBUTE];
  delete nextAttributes[ADULT_URL_ATTRIBUTE];
  // Clean up legacy layout attr that used to mutate host display styles.
  delete nextAttributes[ADULT_BADGE_LAYOUT_ATTRIBUTE];

  return Object.keys(nextAttributes).length ? nextAttributes : null;
};

const stripAdultDecoration = (node: PageNode): PageNode => ({
  ...node,
  nodes: (node.nodes ?? []).filter((child) => !isAdultBadgeNode(child)),
  attributes: stripAdultAttributes(node.attributes ?? null),
});

const applyAdultDecoration = (node: PageNode, url: string): PageNode => {
  const nodes = [...(node.nodes ?? [])];

  if (!nodes.some(isAdultBadgeNode)) {
    nodes.push(AdultBadgeNodeFactory.getNode(node));
  }

  const baseAttributes = stripAdultAttributes(node.attributes ?? null) ?? {};

  return {
    ...node,
    nodes,
    attributes: {
      ...baseAttributes,
      [ADULT_LINK_ATTRIBUTE]: "true",
      [ADULT_URL_ATTRIBUTE]: url,
    },
  };
};

const nodeNeedsAdultDecoration = (node: PageNode, children: PageNode[]): boolean => {
  if (isAdultOpenLink(node) && !isVoidAdultHost(node)) {
    return true;
  }

  return children.some((child) => isAdultOpenLink(child) && isVoidAdultHost(child));
};

const getAdultDecorationUrl = (node: PageNode, children: PageNode[]): string | null => {
  if (isAdultOpenLink(node) && node.onClickAction?.value) {
    return node.onClickAction.value;
  }

  const voidAdultChild = children.find(
    (child) => isAdultOpenLink(child) && isVoidAdultHost(child),
  );

  return voidAdultChild?.onClickAction?.value ?? null;
};

const stripAdultDecorationInPlace = (node: PageNode): boolean => {
  let changed = false;

  const nodes = node.nodes ?? [];
  const filteredNodes = nodes.filter((child) => !isAdultBadgeNode(child));
  if (filteredNodes.length !== nodes.length) {
    node.nodes = filteredNodes;
    changed = true;
  }

  const nextAttributes = stripAdultAttributes(node.attributes ?? null);
  if (!attributesEqual(nextAttributes, node.attributes ?? null)) {
    node.attributes = nextAttributes;
    changed = true;
  }

  return changed;
};

const applyAdultDecorationInPlace = (node: PageNode, url: string): boolean => {
  let changed = false;

  const nodes = node.nodes ?? [];
  if (!nodes.some(isAdultBadgeNode)) {
    node.nodes = [...nodes, AdultBadgeNodeFactory.getNode(node)];
    changed = true;
  }

  const nextAttributes = {
    ...(stripAdultAttributes(node.attributes ?? null) ?? {}),
    [ADULT_LINK_ATTRIBUTE]: "true",
    [ADULT_URL_ATTRIBUTE]: url,
  };
  if (!attributesEqual(nextAttributes, node.attributes ?? null)) {
    node.attributes = nextAttributes;
    changed = true;
  }

  return changed;
};

const syncAdultNodeSelfInPlace = (node: PageNode): boolean => {
  const children = node.nodes ?? [];

  if (nodeNeedsAdultDecoration(node, children)) {
    const url = getAdultDecorationUrl(node, children);
    if (url) {
      return applyAdultDecorationInPlace(node, url);
    }
  }

  return stripAdultDecorationInPlace(node);
};

const syncAdultNodeSubtreeInPlace = (node: PageNode): boolean => {
  let changed = false;

  for (const child of node.nodes ?? []) {
    if (syncAdultNodeSubtreeInPlace(child)) {
      changed = true;
    }

    if (isVoidAdultHost(child) && stripAdultDecorationInPlace(child)) {
      changed = true;
    }
  }

  if (syncAdultNodeSelfInPlace(node)) {
    changed = true;
  }

  return changed;
};

const findNodePath = (node: PageNode, nodeId: string, path: PageNode[] = []): PageNode[] | null => {
  const currentPath = [...path, node];

  if (node.id === nodeId) {
    return currentPath;
  }

  for (const child of node.nodes ?? []) {
    const childPath = findNodePath(child, nodeId, currentPath);
    if (childPath) {
      return childPath;
    }
  }

  return null;
};

const childIdsSignature = (nodes: PageNode[] = []): string =>
  nodes.map((node) => node.id).join(",");

const onClickActionChanged = (
  previousAction?: OnClickAction,
  nextAction?: OnClickAction,
): boolean => {
  if (previousAction === nextAction) {
    return false;
  }

  if (!previousAction || !nextAction) {
    return previousAction !== nextAction;
  }

  return (
    previousAction.type !== nextAction.type ||
    previousAction.value !== nextAction.value ||
    previousAction.isAdultLink !== nextAction.isAdultLink
  );
};

export const needsAdultBadgeSync = (
  previousNode: PageNode | null | undefined,
  nextNode: PageNode,
): boolean => {
  if (!previousNode) {
    return true;
  }

  if (onClickActionChanged(previousNode.onClickAction, nextNode.onClickAction)) {
    return true;
  }

  return childIdsSignature(previousNode.nodes) !== childIdsSignature(nextNode.nodes);
};

export const syncAdultNodeInPlace = (node: PageNode): boolean =>
  syncAdultNodeSubtreeInPlace(node);

export const syncAdultBadgesAlongPath = (root: PageNode, nodeId: string): void => {
  const path = findNodePath(root, nodeId);
  if (!path?.length) {
    return;
  }

  syncAdultNodeSubtreeInPlace(path[path.length - 1]);

  for (let index = path.length - 2; index >= 0; index -= 1) {
    syncAdultNodeSelfInPlace(path[index]);
  }
};

const syncAdultNode = (node: PageNode): PageNode => {
  const syncedChildren = (node.nodes ?? []).map(syncAdultNode);
  const childrenWithoutVoidDecoration = syncedChildren.map((child) =>
    isVoidAdultHost(child) ? stripAdultDecoration(child) : child,
  );

  let updatedNode: PageNode = {
    ...node,
    nodes: childrenWithoutVoidDecoration,
  };

  if (nodeNeedsAdultDecoration(updatedNode, childrenWithoutVoidDecoration)) {
    const url = getAdultDecorationUrl(updatedNode, childrenWithoutVoidDecoration);
    if (url) {
      updatedNode = applyAdultDecoration(updatedNode, url);
    }
  } else {
    updatedNode = stripAdultDecoration(updatedNode);
  }

  return updatedNode;
};

export const clearAdultDecorationsInTree = (nodes: PageNode[]): PageNode[] =>
  nodes.map((node) => {
    const clearedChildren = clearAdultDecorationsInTree(node.nodes ?? []);
    return stripAdultDecoration({ ...node, nodes: clearedChildren });
  });

export const syncAdultBadgesInTree = (nodes: PageNode[]): PageNode[] =>
  nodes.map(syncAdultNode);

export const applyAdultVerifiedUiState = (
  isVerified: boolean,
  isInteractiveView: boolean,
  isPublishCapture = false,
): void => {
  const shouldHideBadges = !isPublishCapture && isInteractiveView && isVerified;

  getAdultVerificationDocuments().forEach((document) => {
    document.getElementById("mainBlock")?.classList.toggle(
      ADULT_VERIFIED_UI_CLASS,
      shouldHideBadges,
    );
  });
};

export const hideAdultBadgesInDom = (): void => {
  applyAdultVerifiedUiState(true, true);
};
